import React, { useState, useMemo } from "react";
import useSWR from "swr";
import { toast } from "sonner";
import { Search, FileText, Upload } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import { Skeleton, StatusPill, Empty } from "@/components/Atoms";

const fetcher = (u) => api.get(u).then((r) => r.data);

export default function CRMDocuments() {
  const { data } = useSWR("/documents", fetcher);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const filtered = useMemo(() => {
    if (!data) return [];
    const ql = q.toLowerCase().trim();
    return data.filter((d) => {
      const matchQ = !ql || (d.document_type || "").toLowerCase().includes(ql) || String(d.customer_flat_id).includes(ql);
      const matchS = !statusFilter || (d.status === statusFilter) || (statusFilter === "available" && d.document_url);
      return matchQ && matchS;
    }).slice(0, 300);
  }, [data, q, statusFilter]);

  return (
    <div className="space-y-5">
      <div>
        <div className="rc-eyebrow">Vault</div>
        <div className="rc-title-1 mt-1">Documents</div>
        <div className="rc-callout">Upload, share and manage customer documents.</div>
      </div>

      <div className="rc-card rc-card-padded">
        <div className="rc-title-3 mb-2 flex items-center gap-2"><Upload size={16} className="text-[var(--rc-blue)]"/>Upload</div>
        <div className="rc-footnote mb-3">Demo placeholder — in production this connects to secured object storage.</div>
        <div className="flex flex-wrap gap-2 items-center">
          <input type="file" className="rc-input !h-10 !w-auto !text-[13px]" disabled/>
          <button onClick={() => toast.success("Demo: upload would be queued")} className="rc-btn rc-btn-sm rc-btn-primary">Upload</button>
        </div>
      </div>

      <div className="rc-card rc-card-padded flex gap-3 flex-wrap items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute top-1/2 -translate-y-1/2 left-4 text-[var(--rc-grey-icon)] pointer-events-none" size={16}/>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by type or flat ID" className="rc-input !h-10 !text-[14px]" style={{ paddingLeft: 44 }} data-testid="docs-search"/>
        </div>
        <select className="rc-input !w-auto !h-10 !text-[14px]" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="docs-status">
          <option value="">All status</option>
          <option value="available">Available</option>
          <option value="pending">Pending</option>
        </select>
        <div className="rc-footnote ml-auto whitespace-nowrap">{filtered.length} docs</div>
      </div>

      {!data && <Skeleton h={300}/>}
      {data && filtered.length === 0 && (
        <div className="rc-card rc-card-padded">
          <Empty icon={FileText} title="No documents" subtitle="Try clearing filters or uploading a new document."/>
        </div>
      )}

      {data && filtered.length > 0 && (
        <>
          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {filtered.map(d => (
              <div key={d.document_id} className="rc-card rc-card-padded flex items-center gap-3" style={{ padding: 14 }}>
                <div className="rc-icon-tile rc-icon-tile-blue" style={{ width: 38, height: 38, borderRadius: 10 }}><FileText size={16}/></div>
                <div className="flex-1 min-w-0">
                  <div className="rc-headline truncate">{d.document_type}</div>
                  <div className="rc-caption">Flat #{d.customer_flat_id} · {d.uploaded_at ? fmtDate(d.uploaded_at) : "—"}</div>
                </div>
                <StatusPill status={d.status || (d.document_url ? "available" : "pending")}/>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block rc-card overflow-x-auto" style={{ padding: 0 }}>
            <table className="w-full text-left" style={{ minWidth: 700 }}>
              <thead className="sticky top-0 bg-white z-10">
                <tr className="rc-caption uppercase">
                  <th className="py-3 px-5 whitespace-nowrap">Customer Flat #</th>
                  <th className="py-3 px-5 whitespace-nowrap">Type</th>
                  <th className="py-3 px-5 whitespace-nowrap">Uploaded</th>
                  <th className="py-3 px-5 whitespace-nowrap">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(d => (
                  <tr key={d.document_id} className="border-t border-[var(--rc-line)]">
                    <td className="py-3 px-5 rc-tabular whitespace-nowrap">{d.customer_flat_id}</td>
                    <td className="py-3 px-5 rc-headline whitespace-nowrap">{d.document_type}</td>
                    <td className="py-3 px-5 rc-footnote whitespace-nowrap">{d.uploaded_at ? fmtDate(d.uploaded_at) : "—"}</td>
                    <td className="py-3 px-5 whitespace-nowrap"><StatusPill status={d.status || (d.document_url ? "available" : "pending")}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
